import React, { useMemo } from 'react'

import PhatDropdown from 'components/formElements/PhatDropdown'
import Checkbox from 'components/formElements/Checkbox'

import styles from './checkboxDropdown.module.scss'

type Props = {
  options: {
    label: React.ReactNode
    id: any
  }[]
  onChange: (selectedOptions: any[]) => void
  placeholder?: string
  includeSelectAllNoneOptions?: boolean
  selectAllLabel?: {
    all: string
    none: string
  }
  className?: string
  menuClassName?: string
  header?: string
  value?: any[]
  selectedOptionsDisplayOverride?: string
}

const CheckboxDropdown = ({
  options,
  onChange,
  placeholder = '',
  includeSelectAllNoneOptions = false,
  selectAllLabel,
  className = '',
  menuClassName = '',
  header,
  value = [],
  selectedOptionsDisplayOverride,
}: Props) => {
  const [selectedIds, setSelectedIds] = React.useState(value)

  const optionsMap: Record<string, React.ReactNode> = useMemo(
    () => options.reduce((acc, option) => ({ ...acc, [option.id]: option.label }), {}),
    [options],
  )

  const handleToggleOption = (id: string) => {
    const updatedSelectedIds = selectedIds.includes(id)
      ? selectedIds.filter(selectedId => selectedId !== id)
      : [...selectedIds, id]

    setSelectedIds(updatedSelectedIds)
    onChange(updatedSelectedIds)
  }

  return (
    <PhatDropdown
      value={null}
      inlineHeader
      header={header}
      triggerWrapperClassName={className}
      menuClassName={menuClassName}
      triggerClassName={styles.options}
      selectedLabel={
        selectedOptionsDisplayOverride ||
        (selectedIds.length ? (
          selectedIds.map(id => (
            <span className={styles.chicklet} key={id}>
              {optionsMap[id] || null}
            </span>
          ))
        ) : (
          <span className={styles.placeholder}>{placeholder}</span>
        ))
      }
      options={[
        {
          icon: 'check circle outline',
          label: selectAllLabel?.all || 'Check all',
          onClick: () => {
            setSelectedIds(options.map(o => o.id))
            onChange(options.map(o => o.id))
          },
          hidden: !includeSelectAllNoneOptions,
          noCloseOnClick: true,
        },
        {
          icon: 'circle outline',
          label: selectAllLabel?.none || 'Uncheck all',
          onClick: () => {
            setSelectedIds([])
            onChange([])
          },
          hidden: !includeSelectAllNoneOptions,
          noCloseOnClick: true,
        },
        { type: 'spacer', hidden: !includeSelectAllNoneOptions },
        ...options.map(o => ({
          noCloseOnClick: true,
          onClick: () => handleToggleOption(o.id),
          label: (
            <span className={styles.option}>
              {o.label}
              <Checkbox
                className={styles.checkbox}
                checked={selectedIds.includes(o.id)}
                onChange={() => handleToggleOption(o.id)}
              />
            </span>
          ),
        })),
      ]}
    />
  )
}

export default CheckboxDropdown
